import { useState } from 'react';

import { getGetStartedUrl } from '../config/global';
import { buildAriBusinessUrl, ARI_BUSINESS_APP_PATH } from '../lib/ari-business-url';
import { useCurrentWorkspace, useWorkspaces, Workspace } from '../hooks/use-workspaces';
import { SoleWorkspace } from './workspace-sole';
import { WorkspaceSwitchOverlay } from './workspace-switch-overlay';
import { WorkspaceSwitcherMenu } from './workspace-switcher-menu';
import { WorkspaceSwitcherTrigger } from './workspace-switcher-trigger';
import { Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CenteredCardText } from './utils';

/**
 * Entry point for the workspace the app is scoped to, mounted in the dashboard's
 * account card and on the account page. Every account has at least one workspace;
 * only an account holding more than one gets a dropdown
 */
export function WorkspaceSwitcher() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { t: translate } = useTranslation();
  const { userWorkspaces, crtWorkspaceData, isLoading, error, refetch } = useWorkspaces();
  const {
    pendingWorkspace,
    switchWorkspace,
    timeoutDialogOpen,
    closeTimeoutDialog,
    switchError,
    closeSwitchErrorDialog,
  } = useCurrentWorkspace();

  const closeMenu = () => setAnchorEl(null);
  const hasSoleWorkspace = userWorkspaces.length === 1;

  // Dismiss the menu before the overlay appears — a popover left open would
  // otherwise sit over the transition, and the user is done with it either way.
  const handleSelect = (workspace: Workspace) => {
    closeMenu();
    switchWorkspace(workspace);
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  if (error !== null) {
    return <CenteredCardText text={translate('workspaces.loadingFailed')} />;
  }

  // An account with no workspace can't do anything in Ari Cloud, so hand off to
  // Ari Business signup rather than dead-ending on a label. Plain link, no
  // return URL — carrying one back through signup is CLOUD-688.
  if (userWorkspaces.length === 0) {
    return (
      <CenteredCardText
        text={translate('workspaces.noWorkspacesFound')}
        action={
          <Button variant="contained" size="small" href={getGetStartedUrl()}>
            {translate('workspaces.createWorkspace')}
          </Button>
        }
      />
    );
  }

  if (hasSoleWorkspace) {
    return (
      <SoleWorkspace
        workspace={userWorkspaces[0]}
        manageUrl={buildAriBusinessUrl(userWorkspaces[0].id, ARI_BUSINESS_APP_PATH)}
      />
    );
  }

  return (
    <>
      <WorkspaceSwitcherTrigger
        workspace={crtWorkspaceData}
        open={Boolean(anchorEl)}
        onOpen={(event) => setAnchorEl(event.currentTarget)}
      />

      <WorkspaceSwitcherMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        onRetry={refetch}
        onSelect={handleSelect}
        // Only the current workspace's row renders a Manage link, so the
        // current workspace is what that link has to carry.
        manageUrl={buildAriBusinessUrl(crtWorkspaceData?.id, ARI_BUSINESS_APP_PATH)}
      />

      <WorkspaceSwitchOverlay
        timeoutDialogOpen={timeoutDialogOpen}
        closeTimeoutDialog={closeTimeoutDialog}
        workspace={pendingWorkspace}
        switchError={switchError}
        closeSwitchErrorDialog={closeSwitchErrorDialog}
      />
    </>
  );
}
